import {
  CheckOutlined,
  CloseOutlined,
  PaperClipOutlined
} from "@ant-design/icons"
import { App, Button, Flex, Form, Select } from "antd"
import React, { type ReactNode, useState } from "react"

import { MODAL_CONFIRM_CONFIG } from "../../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { CHECKLIST_COLUMN_KEYS } from "./columns.tsx"
import {
  buildEditRowCells,
  rowCommitKeyDown
} from "../../../../../../../../components/EntityTable/editable-row-cells.ts"
import { FormChangedWatcher } from "../../../../../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import { useAppSelector } from "../../../../../../../../hooks/use-app-selector.ts"
import { isFormChanged } from "../../../../../../../../utils/object/is-form-changed.ts"
import type { ProjectChecklistItem } from "../../../../../../types/project-checklists.types.tsx"

interface ProjectViewChecklistsMinNewRowProps {
  record: ProjectChecklistItem
  children?: ReactNode
  onSelectRow: (
    record: ProjectChecklistItem,
    action: string
  ) => Promise<void> | void
}

export const ProjectViewChecklistsMinNewRow = ({
  record,
  onSelectRow,
  ...rest
}: ProjectViewChecklistsMinNewRowProps) => {
  const options = useAppSelector((s) => s?.project?.checklistItemsFilterOptions)

  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<ProjectChecklistItem>()
  const { modal } = App.useApp()

  const handleSave = async () => {
    try {
      await form.validateFields()
      const values = form.getFieldsValue()

      setSaving(true)

      await onSelectRow(
        {
          ...record,
          ...values
        },
        "update"
      )
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const action = "cancel"

    const isChanged = isFormChanged<ProjectChecklistItem>(
      form.getFieldsValue(),
      record
    )

    if (isChanged) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk() {
          onSelectRow(record, action)
        }
      })
    } else {
      onSelectRow(record, action)
    }
  }

  const onKeyDownRow = rowCommitKeyDown(() => void handleSave(), {
    busy: saving
  })

  return (
    <Form<ProjectChecklistItem>
      form={form}
      component={false}
      initialValues={record}
      disabled={saving}
    >
      <FormChangedWatcher<ProjectChecklistItem> form={form} saving={saving} />

      <tr {...rest} onKeyDown={onKeyDownRow}>
        {buildEditRowCells(
          rest?.children,
          Object.values(CHECKLIST_COLUMN_KEYS),
          {
            [CHECKLIST_COLUMN_KEYS.isDone]: <td className="ant-table-cell" />,
            [CHECKLIST_COLUMN_KEYS.status]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectChecklistItem> name="status" noStyle>
                  <Select
                    className="w-full"
                    placeholder="Select status"
                    options={options?.status || []}
                  />
                </Form.Item>
              </td>
            ),
            [CHECKLIST_COLUMN_KEYS.attachments]: (
              <td className="ant-table-cell text-center">
                <Button disabled icon={<PaperClipOutlined />} />
              </td>
            )
          },
          <td
            className={`sticky right-0 ant-table-cell ant-table-cell-fix-right ant-table-cell-fix-right-first`}
          >
            <Flex gap="small" justify="center">
              <Button
                variant="outlined"
                color="danger"
                onClick={handleCancel}
                icon={<CloseOutlined />}
              />
              <Button
                loading={saving}
                variant="outlined"
                color="green"
                onClick={handleSave}
                icon={<CheckOutlined />}
              />
            </Flex>
          </td>
        )}
      </tr>
    </Form>
  )
}
